import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Plus, Trash2, Loader2, ShieldCheck, Lock } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { RichTextEditor } from "../components/RichTextEditor";

const CATEGORIES = ["Tech & AI Trends", "Creative Business", "Design & Visual Arts", "Innovation & Startups", "Lifestyle & Culture", "Inspiration & People", "Gaming", "Digital Culture"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublishSubmit() {
  const [params] = useSearchParams();
  const packageKey = params.get("package") || "guest_article";
  const orderParam = params.get("order") || "";
  const { user } = useAuth();
  const nav = useNavigate();

  const [pkgs, setPkgs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const [f, setF] = useState({
    author_name: "", title: "", category: CATEGORIES[0],
    cover_image: "", cover_alt: "", excerpt: "", body: "", website: "",
  });
  const [links, setLinks] = useState([{ url: "", label: "" }]);
  const [agree, setAgree] = useState(false);

  useEffect(() => {
    api.get("/publishing-packages").then((r) => setPkgs(r.data)).catch(() => {});
    if (user) api.get("/publishing/mine").then((r) => setOrders(r.data.orders || [])).catch(() => {});
  }, [user]);

  const pkg = useMemo(() => pkgs.find((p) => p.lookup_key === packageKey), [pkgs, packageKey]);
  const isBundle = (pkg?.credits || 1) > 1;

  // A paid bundle order with remaining credits for this package (if any)
  const creditOrder = useMemo(() => {
    if (orderParam) return orders.find((o) => o.id === orderParam && o.credits_remaining > 0);
    if (isBundle) return orders.find((o) => o.package === packageKey && o.payment_status === "paid" && o.credits_remaining > 0);
    return null;
  }, [orders, orderParam, isBundle, packageKey]);

  const needsBundlePurchase = isBundle && !creditOrder;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const uploadCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setCoverBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, cover_image: `${BACKEND}${data.url}` }));
      toast.success(data.compressed ? "Cover added (optimized)." : "Cover added.");
    } catch (err) { toast.error(`Upload failed: ${apiErr(err)}`); }
    setCoverBusy(false); e.target.value = "";
  };

  const maxLinks = pkg?.max_links || 2;
  const addLink = () => { if (links.length < maxLinks) setLinks([...links, { url: "", label: "" }]); };
  const setLink = (i, k) => (e) => setLinks(links.map((l, j) => (j === i ? { ...l, [k]: e.target.value } : l)));
  const delLink = (i) => setLinks(links.filter((_, j) => j !== i));

  const buyBundle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/publishing/checkout", { package: pkg.lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  const submit = async () => {
    if (!agree) return toast.error("Please agree to the editorial, revision, and refund policies.");
    if (!f.title.trim() || !f.body.trim() || f.body === "<br>") return toast.error("Title and article body are required.");
    if (f.cover_image && !f.cover_alt.trim()) return toast.error("Please add alt text for the cover image.");
    setBusy(true);
    try {
      const payload = {
        package: pkg.lookup_key,
        order_id: creditOrder ? creditOrder.id : "",
        title: f.title, category: f.category, body: f.body,
        cover_image: f.cover_image, cover_alt: f.cover_alt, excerpt: f.excerpt,
        author_name: f.author_name, website: f.website,
        links: links.filter((l) => l.url.trim()),
        agreed_policies: agree,
      };
      const { data } = await api.post("/publishing/submit", payload);
      if (data.needs_payment) {
        const co = await api.post("/publishing/checkout", { order_id: data.order_id, origin_url: window.location.origin });
        window.location.href = co.data.checkout_url;
      } else {
        toast.success("Article submitted for review!");
        nav("/my-publishing");
      }
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  if (!user) {
    return (
      <div className="pti-container py-24 max-w-md text-center" data-testid="publish-login-gate">
        <Lock className="mx-auto text-pti-red" size={44} />
        <h1 className="font-display text-3xl font-black mt-4">Sign in to publish</h1>
        <p className="text-gray-600 mt-2">Create a free account or sign in to submit an article and pay securely.</p>
        <div className="flex gap-3 justify-center mt-6">
          <Link to={`/login?next=${encodeURIComponent(`/publish?package=${packageKey}`)}`} className="bg-pti-red text-white px-6 py-3 font-bold hover:bg-pti-redhover transition-colors">Sign in</Link>
          <Link to="/register" className="border border-pti-black px-6 py-3 font-bold hover:bg-pti-black hover:text-white transition-colors">Register</Link>
        </div>
      </div>
    );
  }

  if (!pkg) return <div className="pti-container py-24 text-center text-gray-500" data-testid="publish-loading">Loading package…</div>;

  return (
    <div className="pti-container py-10 grid lg:grid-cols-12 gap-10" data-testid="publish-submit-page">
      {/* Summary */}
      <aside className="lg:col-span-4 order-2 lg:order-1">
        <div className="border border-gray-200 p-6 lg:sticky lg:top-24" data-testid="publish-summary">
          <span className="text-[11px] uppercase tracking-widest font-bold text-pti-red">Publishing Package</span>
          <h2 className="font-display text-2xl font-bold mt-1">{pkg.name}</h2>
          <div className="font-display text-4xl font-black text-pti-red mt-2">${pkg.price}</div>
          <ul className="mt-4 space-y-2 text-sm">
            {pkg.features.map((ft) => <li key={ft} className="flex gap-2"><Check size={16} className="text-pti-red shrink-0 mt-0.5" /> {ft}</li>)}
          </ul>
          {creditOrder && (
            <div className="mt-4 bg-gray-50 border border-gray-200 p-3 text-sm" data-testid="publish-credit-note">
              Using your Agency Bundle — <strong>{creditOrder.credits_remaining}</strong> submission{creditOrder.credits_remaining === 1 ? "" : "s"} remaining. No extra charge.
            </div>
          )}
          <div className="mt-5 flex items-start gap-2 text-xs text-gray-500">
            <ShieldCheck size={16} className="text-pti-red shrink-0 mt-0.5" />
            <span>Payment does not guarantee editorial approval. Declined submissions that can't meet our standards are refunded in full.</span>
          </div>
          <Link to="/advertise" className="block mt-4 text-sm font-bold text-pti-red">← Change package</Link>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:col-span-8 order-1 lg:order-2">
        <h1 className="font-display text-4xl font-black">Submit your article</h1>

        {needsBundlePurchase ? (
          <div className="mt-6 border border-gray-200 p-6" data-testid="publish-bundle-purchase">
            <p className="text-gray-700">The Agency Publishing Bundle lets you submit up to <strong>{pkg.credits} articles</strong> for multiple clients. Purchase the bundle first, then submit each article from your <Link to="/my-publishing" className="text-pti-red font-bold">My Publishing</Link> area.</p>
            <button onClick={buyBundle} disabled={busy} data-testid="buy-bundle-btn" className="mt-5 bg-pti-red text-white px-6 py-3 font-bold hover:bg-pti-redhover transition-colors disabled:opacity-60 flex items-center gap-2">
              {busy && <Loader2 className="animate-spin" size={16} />} Purchase bundle — ${pkg.price}
            </button>
          </div>
        ) : (
          <div className="mt-6 space-y-5" data-testid="publish-form">
            <Field label={pkg.sponsored ? "Brand / business name" : "Author name"}>
              <input value={f.author_name} onChange={set("author_name")} data-testid="pub-author" placeholder={pkg.sponsored ? "Your brand" : "Your name"} className="pub-input" />
            </Field>
            <Field label="Article title">
              <input value={f.title} onChange={set("title")} data-testid="pub-title" className="pub-input" />
            </Field>
            <Field label="Category">
              <select value={f.category} onChange={set("category")} data-testid="pub-category" className="pub-input">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>

            <Field label="Cover image (optional)">
              <div className="flex items-center gap-3">
                <label data-testid="pub-cover-upload" className="inline-flex items-center gap-2 border border-pti-black px-4 py-2 text-sm font-bold cursor-pointer hover:bg-gray-100 transition-colors">
                  {coverBusy ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Upload cover
                  <input type="file" accept="image/*" onChange={uploadCover} className="hidden" />
                </label>
                {f.cover_image && <span className="text-xs text-green-600 font-bold">Added</span>}
              </div>
              {f.cover_image && <img src={f.cover_image} alt="Cover preview" className="w-full max-h-48 object-contain border border-gray-200 mt-3" />}
            </Field>
            {f.cover_image && (
              <Field label="Cover image alt text (required for accessibility)">
                <input value={f.cover_alt} onChange={set("cover_alt")} data-testid="pub-cover-alt" className="pub-input" />
              </Field>
            )}

            <Field label="Excerpt (optional)">
              <input value={f.excerpt} onChange={set("excerpt")} data-testid="pub-excerpt" placeholder="A one-line summary shown in article cards" className="pub-input" />
            </Field>

            <div>
              <label className="text-sm font-bold">Article body</label>
              <div className="mt-1"><RichTextEditor value={f.body} onChange={(v) => setF((p) => ({ ...p, body: v }))} testid="pub-body" placeholder="Write your article… Use the toolbar to add headings, bold, links, and upload images." /></div>
            </div>

            <Field label="Your website (optional)">
              <input value={f.website} onChange={set("website")} data-testid="pub-website" placeholder="https://yourbusiness.com" className="pub-input" />
            </Field>

            <div>
              <label className="text-sm font-bold">Approved links (up to {maxLinks})</label>
              <p className="text-xs text-gray-500 mt-0.5">Paid links are published with <code>rel="sponsored"</code> for compliance.</p>
              <div className="space-y-2 mt-2">
                {links.map((l, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`pub-link-${i}`}>
                    <input value={l.url} onChange={setLink(i, "url")} placeholder="https://…" data-testid={`pub-link-url-${i}`} className="pub-input flex-1" />
                    <input value={l.label} onChange={setLink(i, "label")} placeholder="Label (optional)" data-testid={`pub-link-label-${i}`} className="pub-input w-40" />
                    {links.length > 1 && <button onClick={() => delLink(i)} data-testid={`pub-link-del-${i}`} className="text-gray-400 hover:text-pti-red"><Trash2 size={16} /></button>}
                  </div>
                ))}
              </div>
              {links.length < maxLinks && <button onClick={addLink} data-testid="pub-add-link" className="mt-2 flex items-center gap-1.5 text-sm font-bold text-pti-red"><Plus size={14} /> Add link</button>}
            </div>

            <label className="flex items-start gap-2 text-sm text-gray-700 border-t border-gray-200 pt-4" data-testid="pub-agree">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="pub-agree-checkbox" className="mt-1 accent-pti-red" />
              <span>I have read and agree to the <Link to="/advertise" className="text-pti-red font-bold">editorial, revision, and refund policies</Link>. I understand payment does not guarantee approval and that sponsored content will be disclosed.</span>
            </label>

            <button onClick={submit} disabled={busy} data-testid="pub-submit-pay" className="w-full sm:w-auto bg-pti-red text-white px-8 py-3 font-bold hover:bg-pti-redhover transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {busy && <Loader2 className="animate-spin" size={16} />}
              {creditOrder ? "Submit for review" : `Submit & pay $${pkg.price}`}
            </button>
          </div>
        )}
      </div>
      <style>{`.pub-input{width:100%;border:1px solid #e5e7eb;padding:0.625rem 0.75rem;outline:none;font-size:0.875rem}.pub-input:focus{border-color:#ef1831}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-bold">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
